// Web port of iOS Features/Home/SessionComposer.swift. Builds an ordered, bulk-
// atomic playback list across 1–3 sets, skipping already-correct questions, with
// ~10% prior-wrong re-injection (normal mode) or a pure wrong drill (wrongOnly).
// Cards (info/cheat/worked) are not inlined on web — they go to bulkAuxiliaryItems
// for the Reference panel (matches the single-set playlist behavior).
import type { QuestionSetItemRead, QuestionSetRead } from "@/api/library-types";
import { CARD_KINDS } from "./playlist";
import { progressStore } from "./progress-store";

export type ComposeMode = "normal" | "wrongOnly" | "puzzleOnly";

export type Focus =
  | { kind: "none" }
  | { kind: "wholeSet"; setId: string }
  | { kind: "topic"; setId: string; topicSlug: string };

export interface PlaybackContext {
  setId: string;
  bulkId: string;
  topicSlug: string;
  bulkGradedSequence: string[];
  /** Originating set's framework slug — gates content features (e.g. word gloss). */
  frameworkSlug?: string | null;
  /** Bulk-level shared passage text (de-trainer reading bulks), when present. */
  sharedPassageText?: string;
}

export interface ComposedItem {
  item: QuestionSetItemRead;
  ctx: PlaybackContext;
}

export interface ComposedSession {
  items: ComposedItem[];
  anchorSetId: string | null;
  bulkAuxiliaryItems: Record<string, QuestionSetItemRead[]>;
  diagnostics: string[];
}

const isGraded = (item: QuestionSetItemRead) => !CARD_KINDS.has(item.item_kind);

interface BulkUnit {
  setId: string;
  bulkId: string;
  topicSlug: string;
  bulkGradedSequence: string[];
  frameworkSlug?: string | null;
  remaining: QuestionSetItemRead[]; // graded items still to do
}

/** Per-set: ordered list of bulk units with their not-yet-correct graded items. */
function bulkUnitsForSet(set: QuestionSetRead, focus: Focus, hideSeenInfo: boolean): BulkUnit[] {
  const itemById = new Map(set.items.map((i) => [i.id, i]));
  const completed = progressStore.completedBulkIds(set.id);
  const seen = progressStore.seenInfo(set.id);
  const cursor = progressStore.cursor(set.id);
  const topicFocus = focus.kind === "topic" && focus.setId === set.id ? focus.topicSlug : null;

  const bulks = [...set.bulks].sort((a, b) => a.order - b.order);
  // Cursor's bulk position in display order, resolved up front (so it survives
  // even if that bulk later lands in completedBulkIds). Mirrors iOS
  // SessionComposer.candidateSequence: normal play never retreats across bulk
  // boundaries — bulks strictly before the cursor are "passed", and their
  // wrong/unanswered items resurface only via the wrong-pool draw / Drill
  // Wrongs, NOT at the front of every new run. Without this, a new session
  // re-serves the first still-incomplete bulk (the opening recap), which reads
  // as "it started from the beginning again".
  const cursorPos = cursor ? bulks.findIndex((b) => b.id === cursor.bulkId) : -1;
  const units: BulkUnit[] = [];

  for (let idx = 0; idx < bulks.length; idx++) {
    const bulk = bulks[idx];
    if (topicFocus && bulk.topic_slug !== topicFocus) continue;
    // Skip mastered bulks unless we're drilling this topic on purpose.
    if (!topicFocus && completed.has(bulk.id)) continue;
    // Skip bulks STRICTLY before the cursor's bulk during normal play (iOS
    // parity). Topic-focus sessions ignore the cursor — the user is jumping to
    // that material on purpose.
    if (!topicFocus && cursorPos >= 0 && idx < cursorPos) continue;

    const graded: string[] = [];
    const remaining: QuestionSetItemRead[] = [];
    let started = !cursor || cursor.bulkId !== bulk.id;
    for (const id of bulk.sequence) {
      const item = itemById.get(id);
      if (!item) continue;
      if (isGraded(item)) {
        graded.push(id);
        // Resume: within the cursor bulk, skip up to & including lastQuestionId.
        if (!started) {
          if (id === cursor!.lastQuestionId) started = true;
          continue;
        }
        if (!topicFocus && progressStore.isCorrect(set.id, id)) continue;
        remaining.push(item);
      } else if (hideSeenInfo && seen.has(id)) {
        // card already seen — skip (it still lands in auxiliary)
      }
    }
    if (remaining.length > 0) {
      units.push({
        setId: set.id,
        bulkId: bulk.id,
        topicSlug: bulk.topic_slug,
        bulkGradedSequence: graded,
        frameworkSlug: set.source_framework_slug,
        remaining,
      });
    }
  }
  return units;
}

/** Per-bulk shared passage text (de-trainer reading bulks), keyed by bulk id. */
function buildSharedPassages(sets: QuestionSetRead[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const set of sets) {
    for (const bulk of set.bulks) {
      const txt = bulk.shared_passage?.text;
      if (txt) out[bulk.id] = txt;
    }
  }
  return out;
}

/** Reference items (info/cheat/worked) per bulk, across all sets. */
function buildAuxiliary(sets: QuestionSetRead[]): Record<string, QuestionSetItemRead[]> {
  const aux: Record<string, QuestionSetItemRead[]> = {};
  for (const set of sets) {
    const itemById = new Map(set.items.map((i) => [i.id, i]));
    for (const bulk of set.bulks) {
      const cards = bulk.sequence
        .map((id) => itemById.get(id))
        .filter((it): it is QuestionSetItemRead => !!it && CARD_KINDS.has(it.item_kind));
      if (cards.length) aux[bulk.id] = cards;
    }
  }
  return aux;
}

function orderSets(sets: QuestionSetRead[], focus: Focus): QuestionSetRead[] {
  if (focus.kind === "wholeSet") return sets.filter((s) => s.id === focus.setId);
  if (focus.kind === "topic") {
    const target = sets.find((s) => s.id === focus.setId);
    return target ? [target] : sets;
  }
  return sets;
}

export function compose(opts: {
  sets: QuestionSetRead[];
  mode: ComposeMode;
  bulkCount: number; // "Sections" for normal
  wrongLimit: number; // for wrongOnly
  focus: Focus;
  hideSeenInfo: boolean;
}): ComposedSession {
  const diagnostics: string[] = [];
  const aux = buildAuxiliary(opts.sets);
  const passageByBulk = buildSharedPassages(opts.sets);
  const anchorSetId = opts.sets[0]?.id ?? null;

  if (opts.sets.length === 0) {
    diagnostics.push("No question sets are selected.");
    return { items: [], anchorSetId, bulkAuxiliaryItems: aux, diagnostics };
  }

  // Global index of every graded item → its context (for wrong injection/drill).
  const indexById = new Map<string, ComposedItem>();
  for (const set of opts.sets) {
    const itemById = new Map(set.items.map((i) => [i.id, i]));
    for (const bulk of set.bulks) {
      const graded = bulk.sequence.filter((id) => {
        const it = itemById.get(id);
        return it && isGraded(it);
      });
      for (const id of graded) {
        const it = itemById.get(id)!;
        indexById.set(id, {
          item: it,
          ctx: {
            setId: set.id,
            bulkId: bulk.id,
            topicSlug: bulk.topic_slug,
            bulkGradedSequence: graded,
            frameworkSlug: set.source_framework_slug,
            sharedPassageText: passageByBulk[bulk.id],
          },
        });
      }
    }
  }

  if (opts.mode === "wrongOnly") {
    const out: ComposedItem[] = [];
    const pools = opts.sets.map((s) => [...progressStore.wrongIds(s.id)]);
    let i = 0;
    while (out.length < opts.wrongLimit && pools.some((p) => p.length)) {
      const pool = pools[i % pools.length];
      const qid = pool.shift();
      if (qid) {
        const ci = indexById.get(qid);
        if (ci) out.push(ci);
      }
      i++;
    }
    if (out.length === 0) diagnostics.push("No wrong answers to drill yet.");
    return { items: out, anchorSetId, bulkAuxiliaryItems: aux, diagnostics };
  }

  if (opts.mode === "puzzleOnly") {
    // Every crossword item across the (focus-ordered) sets, in bulk/sequence
    // order. No cursor/completed gating, no wrong-injection — puzzles replay.
    const out: ComposedItem[] = [];
    for (const set of orderSets(opts.sets, opts.focus)) {
      const itemById = new Map(set.items.map((i) => [i.id, i]));
      for (const bulk of [...set.bulks].sort((a, b) => a.order - b.order)) {
        for (const id of bulk.sequence) {
          const it = itemById.get(id);
          if (it && it.question_type === "crossword") {
            out.push(indexById.get(id) ?? {
              item: it,
              ctx: {
                setId: set.id,
                bulkId: bulk.id,
                topicSlug: bulk.topic_slug,
                bulkGradedSequence: [id],
                frameworkSlug: set.source_framework_slug,
                sharedPassageText: passageByBulk[bulk.id],
              },
            });
          }
        }
      }
    }
    if (out.length === 0) diagnostics.push("No crossword puzzles in the selected sets.");
    return { items: out, anchorSetId, bulkAuxiliaryItems: aux, diagnostics };
  }

  // Normal: bulk-atomic round-robin across (focus-ordered) sets.
  const ordered = orderSets(opts.sets, opts.focus);
  const perSet = ordered.map((s) => bulkUnitsForSet(s, opts.focus, opts.hideSeenInfo));
  const body: ComposedItem[] = [];
  const usedQ = new Set<string>();
  let taken = 0;
  let progress = true;
  while (taken < Math.max(1, opts.bulkCount) && progress) {
    progress = false;
    for (const units of perSet) {
      if (taken >= Math.max(1, opts.bulkCount)) break;
      const unit = units.shift();
      if (!unit) continue;
      progress = true;
      taken++;
      for (const item of unit.remaining) {
        usedQ.add(item.id);
        body.push({
          item,
          ctx: {
            setId: unit.setId,
            bulkId: unit.bulkId,
            topicSlug: unit.topicSlug,
            bulkGradedSequence: unit.bulkGradedSequence,
            frameworkSlug: unit.frameworkSlug,
            sharedPassageText: passageByBulk[unit.bulkId],
          },
        });
      }
    }
  }

  if (body.length === 0) {
    diagnostics.push("Nothing left to practise in the selected sets — try Drill Wrongs or pick another set.");
    return { items: [], anchorSetId, bulkAuxiliaryItems: aux, diagnostics };
  }

  // ~10% prior-wrong injection, evenly spaced.
  const wrongSlots = Math.min(
    Math.max(0, Math.round(body.length * 0.1)),
    20,
  );
  const wrongs: ComposedItem[] = [];
  if (wrongSlots > 0) {
    const pools = opts.sets.map((s) =>
      [...progressStore.wrongIds(s.id)].filter((id) => !usedQ.has(id)),
    );
    let i = 0;
    while (wrongs.length < wrongSlots && pools.some((p) => p.length)) {
      const pool = pools[i % pools.length];
      const qid = pool.shift();
      if (qid) {
        const ci = indexById.get(qid);
        if (ci && !usedQ.has(qid)) {
          usedQ.add(qid);
          wrongs.push(ci);
        }
      }
      i++;
    }
  }

  // Splice wrongs in at evenly-spaced positions.
  const items = [...body];
  if (wrongs.length) {
    const step = Math.floor(items.length / (wrongs.length + 1)) || 1;
    let pos = step;
    for (const w of wrongs) {
      items.splice(Math.min(pos, items.length), 0, w);
      pos += step + 1;
    }
  }

  return { items, anchorSetId, bulkAuxiliaryItems: aux, diagnostics };
}
